import { useCallback, useEffect, useMemo, useState } from "react";
import { getDashboardSalesChart } from "../../../services";
import { DashboardSalesChartResponse } from "../../../types/dashboard.type";
import { DashboardPeriod, PeriodRange } from "../../../utils/dashboardPeriod";
import { ChartMetric } from "./metric";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import SalesBarChart from "../SalesBarChart";
import ChartWithSummarySkeleton from "../Skeleton/ChartWithSummarySkeleton";

interface PeriodSalesChartProps {
  /** "this_month" → day buckets, "this_year" → month buckets. (Never "today".) */
  period: DashboardPeriod;
  metric: ChartMetric;
  dateRange: PeriodRange;
  branchCode: string;
}

/**
 * Period sales-summary graph (section 5). Time-series bars driven by the
 * รายเดือน / รายปี selector + the metric toggle. Presentational only (no Paper):
 * it renders inside the branch-summary card. The per-branch "ยอดรายสาขา" chart
 * is a separate component (BranchSalesChart) and is NOT this one.
 */
export default function PeriodSalesChart({
  period,
  metric,
  dateRange,
  branchCode,
}: PeriodSalesChartProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [chart, setChart] = useState<DashboardSalesChartResponse>();

  const onGetSalesChart = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const {
        data: { data },
      } = await getDashboardSalesChart(
        period,
        metric,
        dateRange.date_from,
        dateRange.date_to,
        branchCode,
      );
      setChart(data);
    } catch {
      setError(true);
      setChart(undefined);
    } finally {
      setLoading(false);
    }
  }, [period, metric, dateRange.date_from, dateRange.date_to, branchCode]);

  useEffect(() => {
    onGetSalesChart();
  }, [onGetSalesChart]);

  const chartData = useMemo(() => {
    if (!chart?.breakdown) return [];
    return chart.breakdown.map((item, index) => ({
      date: item.label,
      amount: item.value,
      dateIndex: index % 7,
      target: item.target,
    }));
  }, [chart?.breakdown]);

  // Dashed target reference line only for the sales metric.
  const saleTarget = useMemo(() => {
    if (metric !== "sales" || !chart?.breakdown?.length) return undefined;
    return chart.breakdown.reduce(
      (max, item) => Math.max(max, item.target ?? 0),
      0,
    );
  }, [metric, chart?.breakdown]);

  const valueUnit =
    metric === "sales" ? "บาท" : metric === "bill_count" ? "ใบ" : "ชิ้น";

  if (loading) return <ChartWithSummarySkeleton />;

  if (error) {
    return (
      <Box
        sx={{
          height: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography color="error" fontWeight={500}>
          ไม่สามารถโหลดข้อมูลได้
        </Typography>
      </Box>
    );
  }

  if (chartData.length === 0) {
    return (
      <Box
        sx={{
          height: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography color="text.secondary" fontWeight={500}>
          ไม่พบข้อมูล
        </Typography>
      </Box>
    );
  }

  return (
    <SalesBarChart
      data={chartData}
      saleTarget={saleTarget}
      colorMode="palette"
      showValueLabels={false}
      valueUnit={valueUnit}
    />
  );
}
